import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import { Sparkles, Compass } from 'lucide-react';

export default function WelcomeScreen({ onChoice }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [animating, setAnimating] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const handleAI = () => {
    setAnimating(true);
    localStorage.setItem('unexa_welcome_choice', 'ai');
    localStorage.setItem('unexa_intent_shown', 'true');
    setTimeout(() => {
      setDismissed(true);
      onChoice('ai');
      // Stay on explore — tour will show, then user decides
    }, 300);
  };

  const handleExplore = () => {
    setAnimating(true);
    localStorage.setItem('unexa_welcome_choice', 'explore');
    localStorage.setItem('unexa_explore_start', Date.now().toString());
    setTimeout(() => {
      setDismissed(true);
      onChoice('explore');
      // Stay on explore — tour will show
    }, 300);
  };

  // Don't render if dismissed
  if (dismissed) return null;

  const firstName = user?.name?.split(' ')[0] || '';

  return (
    <div className="fixed inset-0 z-[10001] flex items-center justify-center" data-testid="welcome-screen">
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900" />

      <div className={`relative flex flex-col items-center px-6 w-full max-w-sm transition-all duration-300 ${animating ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}`}>
        {/* Glowing orb */}
        <div className="mb-2 animate-float">
          <div className="relative">
            <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-blue-500 via-violet-500 to-fuchsia-500 flex items-center justify-center shadow-2xl shadow-violet-500/40">
              <Sparkles className="h-9 w-9 text-white" />
            </div>
            <div className="absolute -inset-4 rounded-3xl bg-gradient-to-br from-blue-500/15 via-violet-500/15 to-fuchsia-500/15 blur-2xl -z-10" />
          </div>
        </div>

        {/* Unexa AI branding */}
        <div className="mb-1">
          <h2 className="text-2xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-violet-400 to-fuchsia-400 tracking-wide text-center">
            Unexa AI
          </h2>
        </div>

        {/* Welcome text */}
        <h1 className="text-3xl font-bold text-white mb-2 text-center mt-4" data-testid="welcome-title">
          {firstName ? `Hey ${firstName}!` : 'Welcome!'}
        </h1>
        <p className="text-white/50 text-sm text-center mb-10 leading-relaxed max-w-xs">
          Your journey starts with Unexa now. How would you like to begin?
        </p>

        {/* Option Cards */}
        <div className="w-full space-y-3">
          {/* AI Guide Option */}
          <button
            onClick={handleAI}
            className="group w-full relative overflow-hidden rounded-2xl p-[1px] transition-all hover:scale-[1.02] active:scale-[0.98]"
            data-testid="welcome-ai-guide"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500 via-violet-500 to-fuchsia-500 rounded-2xl" />
            <div className="relative bg-gradient-to-r from-blue-600/90 via-violet-600/90 to-fuchsia-600/90 rounded-2xl px-5 py-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center shrink-0">
                <Sparkles className="h-6 w-6 text-white" />
              </div>
              <div className="text-left flex-1">
                <p className="text-white font-bold text-sm">Unexa AI Will Guide You</p>
                <p className="text-white/60 text-xs mt-0.5 leading-relaxed">Tell me your goals, I'll find the perfect path</p>
              </div>
              <div className="shrink-0 w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                <span className="text-white text-sm font-bold">&rarr;</span>
              </div>
            </div>
          </button>

          {/* Explore Option */}
          <button
            onClick={handleExplore}
            className="group w-full rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm px-5 py-4 flex items-center gap-4 hover:bg-white/10 hover:border-white/20 transition-all hover:scale-[1.02] active:scale-[0.98]"
            data-testid="welcome-explore"
          >
            <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
              <Compass className="h-6 w-6 text-white/70" />
            </div>
            <div className="text-left flex-1">
              <p className="text-white/90 font-bold text-sm">I'll Explore the App Myself</p>
              <p className="text-white/40 text-xs mt-0.5 leading-relaxed">I know what I want, let me browse the app</p>
            </div>
            <div className="shrink-0 w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
              <span className="text-white/50 text-sm font-bold">&rarr;</span>
            </div>
          </button>
        </div>

        <p className="text-white/20 text-[10px] text-center mt-8">Unexa AI is always available in the bottom navigation</p>
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
        }
        .animate-float { animation: float 3s ease-in-out infinite; }
      `}</style>
    </div>
  );
}
